// =============================================================================
// diaryHelpers.ts
// T-156 — Site Diary shared helpers.
// =============================================================================

const EARTH_R_KM = 6371;

/**
 * Great-circle distance in metres between two lat/lng pairs.
 * Used to compute off_site flag — if the user is more than the threshold
 * away from the project's site coords, we mark the entry as off-site.
 */
export function distanceMeters(
  lat1: number, lng1: number,
  lat2: number, lng2: number,
): number {
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_R_KM * c * 1000;
}

// Off-site threshold — anything beyond ~500m from the project's site coords
// is flagged. Generous enough to handle big sites + parking + the corner
// servo for a quick lunch run, tight enough that a "diary entry from home"
// gets caught.
export const OFF_SITE_THRESHOLD_M = 500;

const MIME_EXT: Record<string, string> = {
  "image/jpeg":      "jpg",
  "image/png":       "png",
  "image/webp":      "webp",
  "image/heic":      "heic",
  "application/pdf": "pdf",
};

export const ACCEPTED_DIARY_PHOTO_MIMES = Object.keys(MIME_EXT).join(",");

export function diaryPhotoPath(projectId: string, diaryId: string, file: File): string {
  const ts = Date.now();
  const ext = MIME_EXT[file.type] ?? (file.name.split(".").pop()?.toLowerCase() ?? "bin");
  return `${projectId}/${diaryId}/eod_${ts}.${ext}`;
}

/** Common Australian carrier outage types. */
export const OUTAGE_CARRIERS = [
  { value: "telstra", label: "Telstra" },
  { value: "nbn",     label: "NBN" },
  { value: "optus",   label: "Optus" },
  { value: "tpg",     label: "TPG / Vodafone" },
  { value: "other",   label: "Other" },
] as const;

/** Outage type codes per carrier (from James — easy to extend later). */
export const OUTAGE_TYPES_BY_CARRIER: Record<string, { value: string; label: string }[]> = {
  telstra: [{ value: "CMART", label: "CMART" }, { value: "other", label: "Other" }],
  nbn:     [{ value: "CRQ",   label: "CRQ"   }, { value: "other", label: "Other" }],
  optus:   [{ value: "other", label: "Other" }],
  tpg:     [{ value: "other", label: "Other" }],
  other:   [{ value: "other", label: "Other" }],
};

// -----------------------------------------------------------------------------
// T-373 — wind speed capture. Dual-unit (km/h + knots) linked inputs; stored in
// conditions jsonb as { wind_kmh, wind_kt }. Colour ranges are INFORMATIONAL
// ONLY (crews work above them when conditions allow — e.g. Nullarbor gusts):
// the red band references the common SWMS ~25kt guidance as an at-a-glance cue,
// never a blocker.
// -----------------------------------------------------------------------------
export const KT_PER_KMH = 1 / 1.852;

export function kmhToKt(kmh: number): number { return Math.round(kmh * KT_PER_KMH * 10) / 10; }
export function ktToKmh(kt: number): number { return Math.round(kt * 1.852 * 10) / 10; }

export type WindBand = "calm" | "moderate" | "high";

/** Band by km/h: <30 calm · 30–45 building · ≥46 (~25kt) high/gusty. */
export function windBand(kmh: number): WindBand {
  if (kmh >= 46) return "high";
  if (kmh >= 30) return "moderate";
  return "calm";
}

export const WIND_BAND_META: Record<WindBand, { label: string; /* tailwind */ chipClass: string; /* pdf hex */ hex: string }> = {
  calm:     { label: "Calm",                 chipClass: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40", hex: "#059669" },
  moderate: { label: "Building",             chipClass: "bg-amber-500/15 text-amber-400 border-amber-500/40",       hex: "#d97706" },
  high:     { label: "High — use judgement", chipClass: "bg-red-500/15 text-red-400 border-red-500/40",             hex: "#dc2626" },
};

export type WeatherChip =
  | "fine" | "overcast" | "wet" | "windy" | "hot" | "cold" | "humid";

export const WEATHER_CHIPS: { value: WeatherChip; label: string }[] = [
  { value: "fine",     label: "Fine" },
  { value: "overcast", label: "Overcast" },
  { value: "wet",      label: "Wet" },
  { value: "windy",    label: "Windy" },
  { value: "hot",      label: "Hot" },
  { value: "cold",     label: "Cold" },
  { value: "humid",    label: "Humid" },
];
